import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Event as EventIcon, Today } from '@mui/icons-material';

import {
  GWG_REMINDER_MODE_DATE,
  GWG_REMINDER_MODE_MEETING,
  IGwGReminder,
  describeReminder,
  formatIsoDate,
  getNextTempId,
} from '../../../Interfaces/IGwGFolder';
import { ILegalTransactionMeeting } from '../../../Interfaces/ILegalTransactionMeeting';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Dokumentart, um die es geht — nur für die Überschrift. */
  documentTypeName: string;
  /**
   * Beteiligter, für den die Erinnerungen gelten. `null` = Standard des Ordners,
   * der für alle Beteiligten ohne eigene Ausnahme greift.
   */
  participantName: string | null;
  reminderArray: IGwGReminder[];
  meetingArray: ILegalTransactionMeeting[];
  /** Ist das Kalender-Modul aktiv und konfiguriert? */
  isCalendarEnabled: boolean;
  isReadOnly: boolean;
  onSave: (reminderArray: IGwGReminder[]) => void;
}

/** Vorschläge für den Abstand zu einem Termin. */
const OFFSET_OPTION_ARRAY = [
  { value: 0, label: 'am Tag des Termins' },
  { value: 3, label: '3 Tage vorher' },
  { value: 7, label: '7 Tage vorher' },
  { value: 14, label: '14 Tage vorher' },
];

/**
 * Tag, an dem eine terminbezogene Erinnerung greift — Termin minus Vorlauf, als
 * ISO-Datum wie das Backend es liefert.
 *
 * Dieselbe Rechnung macht das Backend beim Laden (`DueDate`) und speichert sie
 * bewusst nicht. Hier wird sie wiederholt, damit man beim Einstellen sofort
 * sieht, wann erinnert wird, und nicht erst nach dem Speichern — genau diese
 * Unklarheit stand in der QS-Rückmeldung (#23, Punkt 6).
 *
 * Bewusst mit Mittagszeit gerechnet und über die lokalen Datumsteile
 * zurückgeschrieben: `toISOString()` würde je Zeitzone einen Tag verschieben.
 */
const dueDateIsoForMeeting = (
  meeting: ILegalTransactionMeeting | undefined,
  offsetDays: number
): string | null => {
  const meetingAt = (meeting?.Meeting_at ?? '').slice(0, 10);

  if (meetingAt === '') {
    return null;
  }

  const due = new Date(`${meetingAt}T12:00:00`);

  if (Number.isNaN(due.getTime())) {
    return null;
  }

  due.setDate(due.getDate() - offsetDays);

  const month = String(due.getMonth() + 1).padStart(2, '0');
  const day = String(due.getDate()).padStart(2, '0');

  return `${due.getFullYear()}-${month}-${day}`;
};

const formatMeeting = (meeting: ILegalTransactionMeeting): string => {
  const meetingAt = (meeting.Meeting_at ?? '').slice(0, 10);

  if (meetingAt === '') {
    return meeting.LegalTransactionMeeting;
  }

  const [year, month, day] = meetingAt.split('-');

  return `${meeting.LegalTransactionMeeting} (${day}.${month}.${year})`;
};

/**
 * Erinnerungen einer GwG-Anforderung pflegen.
 *
 * Zwei Arten: ein festes Datum, oder ein Abstand zu einem Termin des Vorgangs.
 * Die terminabhängige Variante ist die nützlichere — verschiebt sich der Termin,
 * wandert die Erinnerung mit, weil das Backend die Fälligkeit rechnet statt sie
 * zu speichern. Sie setzt das Kalender-Modul voraus.
 *
 * Empfänger ist immer die Systemgruppe GwG; Notar und zuständiger Mitarbeiter
 * werden bewusst nicht benachrichtigt. Mehrere Erinnerungen am selben Tag gehen
 * als *eine* Sammelmail raus.
 */
export const GwGReminderDialog: React.FC<IProps> = props => {
  const [reminderArray, setReminderArray] = useState<IGwGReminder[]>([]);

  /**
   * Erinnerung so anreichern, wie das Backend sie nach dem Speichern liefert:
   * mit Terminnamen und gerechnetem Fälligkeitsdatum. Ohne das beschreibt die
   * Zusammenfassung eine gerade eingestellte Erinnerung als „Termin ohne
   * Datum" — beide Angaben entstehen erst serverseitig.
   */
  const withLocalDueDate = (reminder: IGwGReminder): IGwGReminder => {
    if (reminder.ReminderMode !== GWG_REMINDER_MODE_MEETING) {
      return reminder;
    }

    const meeting = props.meetingArray.find(
      entry =>
        entry.idLegalTransactionMeeting === reminder.idLegalTransactionMeeting
    );

    return {
      ...reminder,
      LegalTransactionMeeting:
        reminder.LegalTransactionMeeting ??
        meeting?.LegalTransactionMeeting ??
        null,
      DueDate:
        reminder.DueDate ?? dueDateIsoForMeeting(meeting, reminder.OffsetDays),
    };
  };

  useEffect(() => {
    if (props.isOpen) {
      setReminderArray(props.reminderArray);
    }
  }, [props.isOpen, props.reminderArray]);

  const handleAdd = (mode: string) => {
    const tempId = getNextTempId(
      reminderArray.map(reminder => reminder.idGwGReminder)
    );

    setReminderArray([
      ...reminderArray,
      {
        idGwGReminder: tempId,
        ReminderMode: mode,
        RemindAt: mode === GWG_REMINDER_MODE_DATE ? '' : null,
        idLegalTransactionMeeting:
          mode === GWG_REMINDER_MODE_MEETING
            ? (props.meetingArray[0]?.idLegalTransactionMeeting ?? null)
            : null,
        OffsetDays: mode === GWG_REMINDER_MODE_MEETING ? 7 : 0,
        SentFor: null,
        DueDate: null,
      },
    ]);
  };

  const handleChange = (
    idGwGReminder: number,
    change: Partial<IGwGReminder>
  ) => {
    setReminderArray(
      reminderArray.map(reminder =>
        reminder.idGwGReminder === idGwGReminder
          ? { ...reminder, ...change }
          : reminder
      )
    );
  };

  /** Eine Erinnerung ohne Datum bzw. ohne Termin wird nicht gespeichert. */
  const isIncomplete = (reminder: IGwGReminder): boolean =>
    reminder.ReminderMode === GWG_REMINDER_MODE_MEETING
      ? reminder.idLegalTransactionMeeting === null ||
        reminder.idLegalTransactionMeeting === undefined
      : (reminder.RemindAt ?? '') === '';

  const hasIncomplete = reminderArray.some(isIncomplete);

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>
        Erinnerungen
        <Typography variant='body2' color='text.secondary'>
          {props.documentTypeName}
          {props.participantName === null
            ? ' — Standard für alle Beteiligten'
            : ` — nur für ${props.participantName}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {props.participantName !== null && (
          <Alert severity='info' sx={{ mb: 2 }}>
            Diese Erinnerungen gelten ausschließlich für {props.participantName}
            . Die übrigen Beteiligten behalten den Standard des Ordners.
          </Alert>
        )}

        {reminderArray.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Für diese Unterlage ist keine Erinnerung hinterlegt.
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Jede Zeile ist eine eigene Erinnerung. Es wird zu{' '}
            <strong>jedem</strong> der aufgeführten Zeitpunkte erinnert; fallen
            mehrere auf denselben Tag, geht eine Sammelmail raus.
          </Typography>
        )}

        <Stack spacing={2}>
          {reminderArray.map((reminder, index) => (
            <Box key={`reminder-${reminder.idGwGReminder}`}>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ mb: 0.5 }}
              >
                <Typography variant='caption' color='text.secondary'>
                  {index + 1}. Erinnerung
                </Typography>
                <Chip
                  size='small'
                  variant='outlined'
                  label={
                    reminder.ReminderMode === GWG_REMINDER_MODE_MEETING
                      ? 'terminbezogen'
                      : 'festes Datum'
                  }
                />
              </Stack>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {reminder.ReminderMode === GWG_REMINDER_MODE_MEETING ? (
                  <>
                    <TextField
                      label='Termin'
                      value={reminder.idLegalTransactionMeeting ?? ''}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange(reminder.idGwGReminder, {
                          idLegalTransactionMeeting: Number(event.target.value),
                        })
                      }
                      size='small'
                      select
                      fullWidth
                      disabled={props.isReadOnly}
                      helperText={
                        props.meetingArray.length === 0
                          ? 'Der Vorgang hat noch keinen Termin.'
                          : 'Verschiebt sich der Termin, wandert die Erinnerung mit.'
                      }
                    >
                      {props.meetingArray.map(meeting => (
                        <MenuItem
                          key={`meeting-${meeting.idLegalTransactionMeeting}`}
                          value={meeting.idLegalTransactionMeeting}
                        >
                          {formatMeeting(meeting)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label='Abstand'
                      value={reminder.OffsetDays}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange(reminder.idGwGReminder, {
                          OffsetDays: Number(event.target.value),
                        })
                      }
                      size='small'
                      select
                      sx={{ minWidth: 190 }}
                      disabled={props.isReadOnly}
                      helperText={(() => {
                        const dueDate = formatIsoDate(
                          withLocalDueDate(reminder).DueDate
                        );

                        return dueDate === ''
                          ? 'Der Termin hat noch kein Datum.'
                          : `Erinnerung am ${dueDate}`;
                      })()}
                    >
                      {OFFSET_OPTION_ARRAY.map(option => (
                        <MenuItem
                          key={`offset-${option.value}`}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                ) : (
                  <TextField
                    label='Erinnerung am'
                    type='date'
                    value={(reminder.RemindAt ?? '').slice(0, 10)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(reminder.idGwGReminder, {
                        RemindAt: event.target.value,
                      })
                    }
                    size='small'
                    fullWidth
                    disabled={props.isReadOnly}
                    InputLabelProps={{ shrink: true }}
                    helperText={
                      reminder.SentFor ===
                      (reminder.RemindAt ?? '').slice(0, 10)
                        ? 'Für dieses Datum wurde bereits erinnert.'
                        : undefined
                    }
                  />
                )}

                <IconButton
                  size='small'
                  disabled={props.isReadOnly}
                  onClick={() =>
                    setReminderArray(
                      reminderArray.filter(
                        entry => entry.idGwGReminder !== reminder.idGwGReminder
                      )
                    )
                  }
                  sx={{ mt: 0.5 }}
                >
                  <Delete fontSize='small' />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<Today />}
            disabled={props.isReadOnly}
            onClick={() => handleAdd(GWG_REMINDER_MODE_DATE)}
          >
            Festes Datum
          </Button>
          <Button
            variant='outlined'
            size='small'
            startIcon={<EventIcon />}
            disabled={
              props.isReadOnly ||
              !props.isCalendarEnabled ||
              props.meetingArray.length === 0
            }
            onClick={() => handleAdd(GWG_REMINDER_MODE_MEETING)}
          >
            An einen Termin koppeln
          </Button>
        </Stack>

        {!props.isCalendarEnabled && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 1 }}
          >
            Terminabhängige Erinnerungen setzen das Kalender-Modul voraus.
          </Typography>
        )}

        {props.isCalendarEnabled && props.meetingArray.length === 0 && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 1 }}
          >
            Sobald der Vorgang einen Termin hat, lässt sich eine Erinnerung
            daran koppeln.
          </Typography>
        )}

        {reminderArray.length > 1 && (
          <Alert
            severity='info'
            icon={<Add fontSize='inherit' />}
            sx={{ mt: 2 }}
          >
            Mehrere Erinnerungen am selben Tag werden zu einer E-Mail
            zusammengefasst. Empfänger ist die Systemgruppe GwG.
          </Alert>
        )}

        {reminderArray.length > 0 && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 2 }}
          >
            {reminderArray
              .filter(reminder => !isIncomplete(reminder))
              .map(reminder => describeReminder(withLocalDueDate(reminder)))
              .join(' · ')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={() => props.setIsOpen(false)}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          disabled={props.isReadOnly || hasIncomplete}
          onClick={() => {
            props.onSave(reminderArray);
            props.setIsOpen(false);
          }}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
